<script setup>
import { ref, watch, computed } from 'vue';

import { 
  kPage, 
  kBlockTitle,
  kBlock,
  kList,
  kListItem,
  kListInput,
  kToggle,
  kButton,
  kCheckbox
} from 'konsta/vue';

import { ChatBubbleLeftEllipsisIcon, PlusIcon, InformationCircleIcon, PhoneIcon, UserIcon, EnvelopeIcon, ArrowRightIcon, TagIcon, TrashIcon } from '@heroicons/vue/24/outline'

import {useConfig} from '@/stores/config'
const configStore = useConfig();

import {useBooking} from '@/stores/booking'
const bookingStore = useBooking();

import {usePopup} from '@/stores/popup'
const popupStore = usePopup();

import SocialIcon from '@/components/SocialIcon.vue';

const name_blink = ref(false);
const phone_blink = ref(false);
const email_blink = ref(false);
const promo_blink = ref(false);

const privacy_policy_checked =  ref(false);
const privacy_policy_blink = ref(false);
const send_commercial_checked = ref(false);

const confirm_loading = ref(false)
const field_errors = ref({})
async function confirm(){
  confirm_loading.value = true;
  field_errors.value = {};

  try{
    await bookingStore.confirm({
      send_commercial_checked : send_commercial_checked.value,
    });
  }catch(e){
    const messages = e.response?.data?.message;
    if(e.response?.status === 400 && messages){
      field_errors.value = Object.fromEntries(
        Object.entries(messages).map(([k, v]) => [k, v[0]])
      );
    }else throw e;
  }
  confirm_loading.value = false;
}

async function applyPromo(){
  if(bookingStore.promo_code == '' || bookingStore.promo_code == ' ' || bookingStore.promo_code == null){
    promo_blink.value = true;
    window.setTimeout(_ => promo_blink.value = false, 2000);
    return;
  }

  await bookingStore.applyPromo();
}

var default_name = '';
if(import.meta.env.VITE_DEFAULT_NAME){
  default_name = import.meta.env.VITE_DEFAULT_NAME;
  bookingStore.name = import.meta.env.VITE_DEFAULT_NAME;
}

var default_phone = '';
if(import.meta.env.VITE_DEFAULT_PHONE){
  default_phone = import.meta.env.VITE_DEFAULT_PHONE;
  bookingStore.phone = import.meta.env.VITE_DEFAULT_PHONE;
}

</script>

<template>
  <template v-if="!configStore.loading">

    <k-block-title>Dettagli</k-block-title>
    <k-list dividers strong outline class="list-full-text">

      <k-list-item 
        v-for="os in bookingStore.service_or_bundle?.optional_services"
        header="Opzione">
        <template #media>
          <PlusIcon class="h-5 w-5"/>
        </template>
        <template #title>
          {{ os.name }}
          <div v-if="os.show_prices" class="font-bold text-primary">
            + {{ bookingStore.getOptionalServicePrice(os).toFixed(2) }} €
            <span v-if="os.billing_type === 'per_day'" class="text-xs font-normal opacity-70">
              ({{ os.price_with_tax.toFixed(2) }} € × {{ bookingStore.bnb_nights }} notti)
            </span>
          </div>
        </template>
        <template #after>
          <k-toggle 
            :checked="os.checked"
            @change="() => (os.checked = !os.checked)"/>
        </template>
      </k-list-item>

      <k-list-input 
        v-if="bookingStore.appointment.name_required"
        label="Nome e Cognome"
        type="text" 
        input-class="border-0"
        :class="name_blink ? 'blink' : ''"
        :error="field_errors.name"
        placeholder="Inserisci il tuo nome per conferma"
        @change="e => {
          bookingStore.name = e.target.value;
          name_blink = e.target.value == '' || e.target.value == ' ';
        }">
        <template #media>
          <UserIcon class="h-5 w-5" />
        </template>
      </k-list-input>

      <k-list-input 
        v-if="bookingStore.appointment.phone_required && configStore.account.flag_show_phone"
        label="Numero di telefono"
        type="text" 
        input-class="border-0"
        :class="phone_blink ? 'blink' : ''"
        :error="field_errors.phone"
        placeholder="Inserisci il numero per conferma"
        @change="e => {
          bookingStore.phone = e.target.value;
          phone_blink = e.target.value == '' || e.target.value == ' ';
        }">
        <template #media>
          <PhoneIcon class="h-5 w-5" />
        </template>
      </k-list-input>

      <k-list-input 
        v-if="bookingStore.appointment.email_required && configStore.account.flag_show_email"
        label="Email"
        type="text" 
        input-class="border-0"
        :class="email_blink ? 'blink' : ''"
        :error="field_errors.email"
        placeholder="Inserisci la email per conferma"
        @change="e => {
          bookingStore.email = e.target.value;
          email_blink = e.target.value == '' || e.target.value == ' ';
        }">
        <template #media>
          <EnvelopeIcon class="h-5 w-5" />
        </template>
      </k-list-input>

      <k-list-input 
        label="Note" 
        type="textarea" 
        input-class="!h-20 resize-none border-0"
        placeholder="Aggiungi eventuali note per l'appuntamento"
        @change="e => bookingStore.note = e.target.value ">
        <template #media>
          <ChatBubbleLeftEllipsisIcon class="h-5 w-5" />
        </template>
      </k-list-input>
    </k-list>


    <k-list dividers strong outline class="list-full-text">
      <k-list-item v-if="!bookingStore.promo">
        <template #media>
          <TagIcon class="h-5 w-5" />
        </template>
        <template #header>
          <div :class="promo_blink ? 'blink' : ''">
            Applica codice sconto
          </div>
        </template>
        <template #title>
          <input
            type="text"
            v-model="bookingStore.promo_code"
            placeholder="Inserisci il codice"
            :class="['w-full border-0 focus:ring-0 bg-transparent p-0', promo_blink ? 'blink' : '']"
          />
        </template>
        <template #after>
          <button
            @click="applyPromo"
            :disabled="bookingStore.promo_loading"
            class="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg bg-primary text-white disabled:bg-gray-200 disabled:text-gray-400 transition-colors">
            <ArrowRightIcon class="h-4 w-4" />
          </button>
        </template>
      </k-list-item>

      <k-list-item v-else>
        <template #media>
          <TagIcon class="h-5 w-5" />
        </template>
        <template #header>
          Codice sconto applicato
        </template>
        <template #title>
          {{ bookingStore.promo.code }}
        </template>
        <template #after>
          <button
            @click="bookingStore.removePromo()"
              class="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg bg-primary text-white disabled:bg-gray-200 disabled:text-gray-400 transition-colors">
            <TrashIcon class="h-5 w-5" />
          </button>
        </template>
      </k-list-item>

      <k-list-item v-if="bookingStore.promo_status" class="text-sm font-bold">
        <template #title>
          <span v-if="bookingStore.promo_status === 'success'" class="text-green-600">
            Codice sconto applicato correttamente
          </span>
          <span v-else-if="bookingStore.promo_status === 'error'" class="text-red-600">
            Codice sconto non valido
          </span>
          <span v-else-if="bookingStore.promo_status === 'expired'" class="text-orange-500">
            Codice sconto scaduto
          </span>
        </template>
      </k-list-item>
    </k-list>

    <k-block 
      v-if="configStore.config.booking_info" 
      strong 
      inset 
      class="text-center bg-primary-light text-primary leading-none">
      <InformationCircleIcon class="h-5 w-5 mb-2 mx-auto"/>

      <div v-html="configStore.config.booking_info"/>
      <br/>
      <a 
        v-if="configStore.config.terms_conditions"
        href="#" 
        @click="popupStore.open('terms_conditions')"
        class="font-bold">
        Leggi tutte le condizioni
      </a>
    </k-block>


    <k-list dividers strong outline>
      <k-list-item 
        label
        >
        <template #media>
          <k-checkbox
            :checked="privacy_policy_checked"
            @change="e => {
              privacy_policy_checked = e.target.checked
              if(privacy_policy_checked) privacy_policy_blink = false;
            }"
          />

        </template>
        <template #header>
          <div :class="privacy_policy_blink ? 'blink' : ''">
            Acconsento al trattamento dei dati personali per l’esecuzione dei servizi richiesti, avendo letto la <a class="text-primary" @click="popupStore.open('privacy_policy')">privacy policy</a>.
          </div>
        </template>
      </k-list-item>

      <k-list-item 
        label>

        <template #media>
          <k-checkbox
            :checked="send_commercial_checked"
            @change="e => send_commercial_checked = e.target.checked"
          />

        </template>

        <template #header>
          Accetto di ricevere <a class="text-primary" @click="popupStore.open('privacy_policy')">informazioni e offerte commerciali</a> da {{ configStore.account.name }}
        </template>
      </k-list-item>
    </k-list>

    <k-block 
      v-if="bookingStore.appointment.phone_required && configStore.account?.flag_enable_whatsapp_reminder" 
      strong 
      inset 
      class="text-center bg-[#def4e3] text-primary leading-none">
      <SocialIcon type="whatsapp" class="h-10 w-10 mx-auto mb-3"/>

      Ti invieremo una conferma prenotazione al numero 
      <strong v-if="bookingStore.phone != '' && bookingStore.phone != ' '">{{ bookingStore.phone }}</strong>
      <template v-else>indicato</template>
      tramite <strong>WhatsApp</strong>.

    </k-block>

    <k-block>
      <k-button 
        large
        :class="[
          !privacy_policy_checked ? 'bg-gray-200' : ''
        ]"
        @click="_ => {
          if(bookingStore.appointment.name_required && (bookingStore.name == '' || bookingStore.name == ' ')){
            name_blink = true;
            window.setTimeout(_=> name_blink = false, 2000);
          }

          if(bookingStore.appointment.phone_required && (bookingStore.phone == '' || bookingStore.phone == ' ')){
            phone_blink = true;
            window.setTimeout(_=> phone_blink = false, 2000);
          }

          if(bookingStore.appointment.email_required && (bookingStore.email == '' || bookingStore.email == ' ')){
            email_blink = true;
            window.setTimeout(_=> email_blink = false, 2000);
          }
          
          if(!privacy_policy_checked){
            privacy_policy_blink = true;
            window.setTimeout(_=> privacy_policy_blink = false, 2000);
          }
          
          if(privacy_policy_checked && (
            (!bookingStore.appointment.name_required && !bookingStore.appointment.phone_required && !bookingStore.appointment.email_required) 
            || (bookingStore.phone != '' && bookingStore.phone != ' ' && bookingStore.name != '' && bookingStore.name != ' ')
          ))
          confirm();
        }"
        >
        {{ configStore.config.is_payment_enabled && bookingStore.appointment?.price_with_tax > 0 ? 'Procedi al pagamento' : 'Conferma prenotazione' }}
      </k-button>

      <k-button 
        @click="bookingStore.goBackStep()" 
        class="!bg-transparent !text-gray-500 !text-xs mt-5">
        Indietro
      </k-button>
    </k-block>
  </template>

</template>


<style lang="scss">
.blink{
  animation: blinker 1s linear infinite;
  @apply text-red-600;

  .text-black{
    @apply text-red-600;
  }
}

@keyframes blinker {
  50% {
    opacity: 0;
  }
}
</style>